import { prefs } from "./stores";
const hasPerformance = typeof performance !== 'undefined' && performance && performance.now;
const now = () => hasPerformance ? performance.now() : Date.now();
const noopEnd = () => 0;

const stats = {};
const recent = [];
const counters = {};
const sampleCounters = {};
let enabled = false;

const MAX_RECENT = 80;
const DEFAULT_SLOW_MS = 16;

function ensure(name){
    if(!stats[name]){
        stats[name] = {
            count: 0,
            total: 0,
            max: 0,
            slow: 0
        };
    }
    return stats[name];
}

function remember(name, duration, meta){
    if(duration < DEFAULT_SLOW_MS) return;
    recent.push({
        name,
        duration: Number(duration.toFixed(2)),
        at: new Date().toISOString(),
        meta
    });
    if(recent.length > MAX_RECENT) recent.shift();
}

prefs.subscribe(p => {
    let nextEnabled = !!(p && p.performanceMeasures);
    if(enabled && !nextEnabled) resetPerf();
    enabled = nextEnabled;
});

export function perfEnabled(){
    return enabled;
}

export function perfCount(name, amount = 1){
    if(!enabled) return;
    counters[name] = (counters[name] || 0) + amount;
}

export function perfStart(name, meta = undefined){
    if(!enabled) return noopEnd;
    const start = now();
    return (extraMeta = undefined) => {
        const duration = now() - start;
        const s = ensure(name);
        s.count++;
        s.total += duration;
        s.max = Math.max(s.max, duration);
        if(duration >= DEFAULT_SLOW_MS) s.slow++;
        remember(name, duration, extraMeta || meta);
        return duration;
    };
}

export function perfMeasure(name, fn, meta = undefined){
    if(!enabled) return fn();
    const end = perfStart(name, meta);
    try{
        const result = fn();
        if(result && typeof result.then == 'function'){
            return result.finally(() => end());
        }
        end();
        return result;
    }catch(e){
        end({error: e && e.message ? e.message : e});
        throw e;
    }
}

export async function perfMeasureAsync(name, fn, meta = undefined){
    if(!enabled) return await fn();
    const end = perfStart(name, meta);
    try{
        return await fn();
    }finally{
        end();
    }
}

export function perfSampleMeasure(name, every, fn, meta = undefined){
    if(!enabled) return fn();
    perfCount(`${name}.calls`);
    sampleCounters[name] = (sampleCounters[name] || 0) + 1;
    if(sampleCounters[name] % every != 0) return fn();
    return perfMeasure(`${name}.sample`, fn, meta);
}

export function resetPerf(){
    for(let k of Object.keys(stats)) delete stats[k];
    for(let k of Object.keys(counters)) delete counters[k];
    for(let k of Object.keys(sampleCounters)) delete sampleCounters[k];
    recent.splice(0, recent.length);
}

function sortedStats(){
    return Object.entries(stats)
        .map(([name, s]) => ({
            name,
            count: s.count,
            total: Number(s.total.toFixed(2)),
            avg: Number((s.total / Math.max(1, s.count)).toFixed(2)),
            max: Number(s.max.toFixed(2)),
            slow: s.slow
        }))
        .sort((a, b) => b.total - a.total);
}

export function getPerfSnapshot(context = {}){
    return {
        generatedAt: new Date().toISOString(),
        enabled,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        context,
        stats: sortedStats(),
        counters: {...counters},
        recentSlowEvents: [...recent].reverse()
    };
}

export function formatPerfReport(context = {}){
    const snapshot = getPerfSnapshot(context);
    return JSON.stringify(snapshot, null, 2);
}

if(typeof window !== 'undefined'){
    window.STCC_PERF = {
        enabled: perfEnabled,
        report: formatPerfReport,
        snapshot: getPerfSnapshot,
        reset: resetPerf
    };
}
